import React, { useCallback } from 'react';
import {
  buildPendingInsuranceCoverageRequest,
  buildSelfPayCoverageRequest,
  isSelfPayCoverage,
  useCreatePatientCoverageMutation,
  useUpdatePatientCoverageMutation,
} from '@features/patient-coverages/store';
import { useSubjectLanguageVariants } from '@features/user';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import SentryService from '@services/SentryService';
import { useSnackbar } from 'notistack';

import type { PatientCoverage } from '../types';

interface UseSelfPayCoverageDesignationArgs {
  patientId: string;
  primaryCoverage?: Partial<PatientCoverage> | null;
  refetchCoverages: () => Promise<unknown> | unknown;
  onCoverageSave?: () => void;
  onAfterSave?: () => void;
}

export function useSelfPayCoverageDesignation({
  patientId,
  primaryCoverage,
  refetchCoverages,
  onCoverageSave,
  onAfterSave,
}: UseSelfPayCoverageDesignationArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const [createCoverage] = useCreatePatientCoverageMutation();
  const [updateCoverage] = useUpdatePatientCoverageMutation();
  const isSelfPay = isSelfPayCoverage(primaryCoverage);

  const markSelfPay = useCallback(async () => {
    try {
      if (primaryCoverage?.patient_insurance_coverage_id) {
        await updateCoverage({
          patientId,
          coverageId: primaryCoverage.patient_insurance_coverage_id,
          updateCoverageRequest: buildSelfPayCoverageRequest(),
        }).unwrap();
      } else {
        await createCoverage({
          patientId,
          createCoverageRequest: buildSelfPayCoverageRequest(),
        }).unwrap();
      }

      onAfterSave?.();
      await refetchCoverages();
      onCoverageSave?.();
    } catch (error) {
      SentryService.captureException(error, {
        level: 'error',
        tags: { patient_id: patientId, action: 'mark_self_pay' },
        extra: { error },
      });
      enqueueSnackbar('Failed to mark self-pay', { variant: 'error' });
    }
  }, [
    createCoverage,
    enqueueSnackbar,
    onAfterSave,
    onCoverageSave,
    patientId,
    primaryCoverage?.patient_insurance_coverage_id,
    refetchCoverages,
    updateCoverage,
  ]);

  const switchToInsurance = useCallback(async () => {
    if (!primaryCoverage?.patient_insurance_coverage_id) return;

    try {
      await updateCoverage({
        patientId,
        coverageId: primaryCoverage.patient_insurance_coverage_id,
        updateCoverageRequest: buildPendingInsuranceCoverageRequest(),
      }).unwrap();

      onAfterSave?.();
      await refetchCoverages();
      onCoverageSave?.();
    } catch (error) {
      SentryService.captureException(error, {
        level: 'error',
        tags: { patient_id: patientId, action: 'switch_self_pay_to_insurance' },
        extra: { error },
      });
      enqueueSnackbar('Failed to switch to insurance', { variant: 'error' });
    }
  }, [
    enqueueSnackbar,
    onAfterSave,
    onCoverageSave,
    patientId,
    primaryCoverage?.patient_insurance_coverage_id,
    refetchCoverages,
    updateCoverage,
  ]);

  const handleSelfPayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        void markSelfPay();
      } else {
        void switchToInsurance();
      }
    },
    [markSelfPay, switchToInsurance],
  );

  return {
    isSelfPay,
    handleSelfPayChange,
    markSelfPay,
    switchToInsurance,
  };
}

interface SelfPayCoverageToggleProps extends UseSelfPayCoverageDesignationArgs {
  description?: React.ReactNode;
  descriptionVariant?: TypographyProps['variant'];
  containerSx?: SxProps<Theme>;
  descriptionSx?: SxProps<Theme>;
}

export function SelfPayCoverageToggle({
  description,
  descriptionVariant = 'body2',
  containerSx,
  descriptionSx,
  ...designationArgs
}: SelfPayCoverageToggleProps) {
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const { isSelfPay, handleSelfPayChange } = useSelfPayCoverageDesignation(designationArgs);

  return (
    <Box sx={containerSx}>
      <FormControlLabel
        control={<Checkbox checked={isSelfPay} onChange={handleSelfPayChange} />}
        label={`${subjectLanguageVariants.singularCap} is self-pay`}
      />
      <Typography
        variant={descriptionVariant}
        color="text.secondary"
        sx={{ ml: 4, ...descriptionSx }}
      >
        {description ??
          `Use this only when this ${subjectLanguageVariants.singular} should be billed directly instead of submitting to insurance.`}
      </Typography>
    </Box>
  );
}

interface SelfPayCoverageCardProps {
  subjectLabel?: string;
  description?: React.ReactNode;
  descriptionVariant?: TypographyProps['variant'];
}

export function SelfPayCoverageCard({
  subjectLabel,
  description,
  descriptionVariant = 'body2',
}: SelfPayCoverageCardProps) {
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const resolvedSubjectLabel = subjectLabel ?? subjectLanguageVariants.singular;

  return (
    <Alert severity="info" sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Self-pay
      </Typography>
      <Typography variant={descriptionVariant} color="text.secondary">
        {description ??
          `This ${resolvedSubjectLabel} is designated as self-pay. Payer eligibility and claim submission prompts should stay quiet until insurance is added.`}
      </Typography>
    </Alert>
  );
}
